import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import UnifiedCard from '../../common/Card/UnifiedCard';
import FilterRail from './FilterRail';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import Icon from '../../common/Icon/Icon';
import './OneSearch.css';

/**
 * ONE SEARCH - the site's front door.
 *
 * The URL is the source of truth: query, filters, radius, the open
 * card window, and the who-has/who-wants pages all live in search
 * params. Back/forward restores every step, and any state is
 * shareable/bookmarkable (/search?q=krenko&card=...&view=have).
 *
 * The filter rail lives in FilterRail.js (game sections are config-
 * driven from the server). Filters follow into the card window, so
 * a tile's printings count and its click-through always agree.
 *
 * Empty box = the trade-world feed: your binder matches first (more
 * owners = higher), then everything the community has or wants,
 * scattered. It ends when the community's cards end - no filler.
 */

const SORTS = [
    ['relevance', 'Relevance'],
    ['price-desc', 'Price: high to low'],
    ['price-asc', 'Price: low to high'],
    ['name', 'Name A-Z'],
];

function OneSearch({ initialQuery = '' }) {
    const { user } = useUser();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // ---------- URL-derived state ----------
    const q = searchParams.get('q') || '';
    const near = Math.max(0, parseInt(searchParams.get('near')) || 0);
    const drillName = searchParams.get('card') || null;
    const viewKind = ['have', 'want'].includes(searchParams.get('view')) ? searchParams.get('view') : null;
    const viewUuid = searchParams.get('printing') || null;
    const filters = useMemo(() => ({
        sort: ['price-desc', 'price-asc', 'name'].includes(searchParams.get('sort')) ? searchParams.get('sort') : 'relevance',
        rarities: (searchParams.get('rarities') || '').split(',').filter(Boolean),
        finishes: (searchParams.get('finishes') || '').split(',').filter(Boolean),
        sets: (searchParams.get('sets') || '').split(',').filter(Boolean),
        priceMin: searchParams.get('pmin') || '',
        priceMax: searchParams.get('pmax') || '',
        avail: ['have', 'want'].includes(searchParams.get('avail')) ? searchParams.get('avail') : '',
    }), [searchParams]);

    // Engaging ANY filter starts a search even with an empty box
    // (browse mode) - a clicked filter always visibly does something
    const canSearch = q.trim().length >= 2 || !!filters.avail
        || filters.sets.length > 0 || filters.rarities.length > 0 || filters.finishes.length > 0
        || filters.priceMin !== '' || filters.priceMax !== '';

    // ---------- local state (fetched data + UI chrome) ----------
    const [data, setData] = useState(null);
    const [cards, setCards] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [printings, setPrintings] = useState(null);
    const [detail, setDetail] = useState({});
    const [feed, setFeed] = useState(null); // null = not loaded yet
    const [feedHasMore, setFeedHasMore] = useState(false);
    const [feedLoading, setFeedLoading] = useState(false);
    const [railKey, setRailKey] = useState(0); // bump remounts the rail (Reset)
    const [sheetOpen, setSheetOpen] = useState(false); // mobile filter sheet
    const [allSets, setAllSets] = useState([]);
    const sentinelRef = useRef(null);
    const stateRef = useRef({});
    stateRef.current = {
        q, near, filters, canSearch, hasMore, loadingMore, loading, cards,
        drill: drillName, feed: feed || [], feedHasMore, feedLoading,
    };

    // Patch search params in place; push=true adds a history entry
    // (drilling into a card / opening a people page), otherwise replace.
    const updateParams = useCallback((patch, push = false) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(patch).forEach(([k, v]) => {
                if (v === null || v === undefined || v === '') next.delete(k);
                else next.set(k, String(v));
            });
            return next;
        }, { replace: !push });
    }, [setSearchParams]);

    // Toggle one value inside a comma-list param (atomic on prev URL,
    // so rapid clicks can't drop each other)
    const toggleListParam = useCallback((key, value) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            const list = (next.get(key) || '').split(',').filter(Boolean);
            const updated = list.includes(value) ? list.filter(x => x !== value) : [...list, value];
            if (updated.length) next.set(key, updated.join(','));
            else next.delete(key);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // ---------- fetching ----------
    const buildParams = (query, radius, f, offset) => {
        const p = new URLSearchParams({ q: query.trim() });
        if (radius) p.set('near', radius);
        if (offset) p.set('offset', offset);
        if (f.rarities.length) p.set('rarities', f.rarities.join(','));
        if (f.sets.length) p.set('setCodes', f.sets.join(','));
        if (f.finishes.length) p.set('finishes', f.finishes.join(','));
        if (f.sort !== 'relevance') p.set('sort', f.sort);
        if (f.priceMin !== '') p.set('priceMin', f.priceMin);
        if (f.priceMax !== '') p.set('priceMax', f.priceMax);
        if (f.avail) p.set('avail', f.avail);
        return p.toString();
    };

    const search = useCallback(async (query, radius, f, offset = 0) => {
        offset === 0 ? setLoading(true) : setLoadingMore(true);
        try {
            const res = await fetch(`/api/onesearch?${buildParams(query, radius, f, offset)}`, { credentials: 'include' });
            if (res.ok) {
                const d = await res.json();
                if (offset === 0) { setData(d); setCards(d.cards); }
                else setCards(prev => [...prev, ...d.cards]);
                setHasMore(!!d.hasMore);
            }
        } catch { }
        finally { setLoading(false); setLoadingMore(false); }
    }, []);

    const loadFeed = useCallback(async (offset = 0) => {
        setFeedLoading(true);
        try {
            const res = await fetch(`/api/onesearch/feed${offset ? `?offset=${offset}` : ''}`, { credentials: 'include' });
            if (res.ok) {
                const d = await res.json();
                setFeed(prev => (offset === 0 ? d.cards : [...(prev || []), ...d.cards]));
                setFeedHasMore(!!d.hasMore);
            }
        } catch { }
        finally { setFeedLoading(false); }
    }, []);

    // Boot once the user context hydrates (it arrives async on a hard
    // refresh). Loads the feed; writes the homepage-hero query to the URL.
    const bootedRef = useRef(false);
    useEffect(() => {
        if (!user || bootedRef.current) return;
        bootedRef.current = true;
        loadFeed(0);
        if (initialQuery && !q) updateParams({ q: initialQuery });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Run the search whenever the URL's query/filters/radius change
    // (typing, filter clicks, back/forward, shared links). Debounced
    // via effect cleanup.
    const searchKey = [q, near, filters.sort, filters.rarities.join(','), filters.finishes.join(','),
        filters.sets.join(','), filters.priceMin, filters.priceMax, filters.avail].join('|');
    useEffect(() => {
        if (!user) return;
        if (!canSearch) {
            setData(null); setCards([]); setHasMore(false);
            return;
        }
        const t = setTimeout(() => search(q, near, filters), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, searchKey]);

    // Card window: fetch printings when the URL says a card is open.
    // Active filters (sets/rarity/finish/price, and availability) follow
    // into the window, so it always shows exactly what the tile counted.
    const filterKey = [filters.sets.join(','), filters.rarities.join(','), filters.finishes.join(','),
        filters.priceMin, filters.priceMax, filters.avail].join('|');
    useEffect(() => {
        if (!user || !drillName) { setPrintings(null); return; }
        let stale = false;
        setPrintings(null);
        window.scrollTo(0, 0);
        (async () => {
            try {
                const p = new URLSearchParams({ name: drillName });
                if (near) p.set('near', near);
                if (filters.sets.length) p.set('setCodes', filters.sets.join(','));
                if (filters.rarities.length) p.set('rarities', filters.rarities.join(','));
                if (filters.finishes.length) p.set('finishes', filters.finishes.join(','));
                if (filters.priceMin !== '') p.set('priceMin', filters.priceMin);
                if (filters.priceMax !== '') p.set('priceMax', filters.priceMax);
                if (filters.avail) p.set('avail', filters.avail);
                const res = await fetch(`/api/onesearch/printings?${p}`, { credentials: 'include' });
                if (res.ok && !stale) setPrintings(await res.json());
            } catch { }
        })();
        return () => { stale = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, drillName, near, filterKey]);

    // People page: fetch when the URL opens one (cached)
    const detailKey = drillName && viewKind ? `${drillName}|${viewUuid || ''}|${viewKind}|${near}` : null;
    useEffect(() => {
        if (!user || !detailKey || detail[detailKey]) return;
        window.scrollTo(0, 0);
        (async () => {
            try {
                const res = await fetch(
                    `/api/onesearch/detail?name=${encodeURIComponent(drillName)}&kind=${viewKind}` +
                    `${viewUuid ? `&uuid=${viewUuid}` : ''}${near ? `&near=${near}` : ''}`,
                    { credentials: 'include' }
                );
                const d = res.ok ? await res.json() : {};
                setDetail(prev => ({ ...prev, [detailKey]: d }));
            } catch {
                setDetail(prev => ({ ...prev, [detailKey]: {} }));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, detailKey]);

    // Sets list, lazy: when the rail's set box gets focus, or when the
    // URL already carries set codes (the filter chips need their names)
    const setsRequested = useRef(false);
    const ensureSets = useCallback(() => {
        if (setsRequested.current) return;
        setsRequested.current = true;
        fetch('/api/sets')
            .then(r => (r.ok ? r.json() : []))
            .then(d => setAllSets(Array.isArray(d) ? d : []))
            .catch(() => { setsRequested.current = false; });
    }, []);
    useEffect(() => {
        if (filters.sets.length) ensureSets();
    }, [filters.sets.length, ensureSets]);

    // Infinite scroll: pages search results, or the feed when the box
    // is empty. The sentinel div UNMOUNTS whenever the card window is
    // open (drillName), so the observer must re-attach to the fresh
    // node when it closes - watching the old detached node kills the
    // scroll silently. Re-attaching on every append also re-fires the
    // callback while the sentinel sits inside the 400px lookahead, so
    // short pages chain until the viewport is filled.
    useEffect(() => {
        if (!user || drillName) return;
        const el = sentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) => {
            const s = stateRef.current;
            // Batched deliveries arrive oldest-first: any intersecting
            // entry counts, not just the first
            if (!entries.some(e => e.isIntersecting) || s.drill) return;
            if (s.canSearch) {
                if (s.hasMore && !s.loadingMore && !s.loading) {
                    search(s.q, s.near, s.filters, s.cards.length);
                }
            } else if (s.feedHasMore && !s.feedLoading) {
                loadFeed(s.feed.length);
            }
        }, { rootMargin: '400px' });
        io.observe(el);
        return () => io.disconnect();
    }, [search, loadFeed, user, drillName, cards.length, feed?.length]);

    // ---------- actions (all write to the URL) ----------
    const onType = (value) => {
        // typing exits any card window / people page
        updateParams({ q: value, card: null, printing: null, view: null });
    };

    const resetAll = () => {
        setSearchParams(new URLSearchParams(), { replace: true });
        setRailKey(k => k + 1); // remount the rail: clears its set search box
        setSheetOpen(false);
    };

    const openPrintings = (name) => updateParams({ card: name, printing: null, view: null }, true);
    const closeWindow = () => updateParams({ card: null, printing: null, view: null }, true);
    const openView = (kind, uuid) => updateParams({ view: kind, printing: uuid || null }, true);
    const closeView = () => updateParams({ view: null, printing: null }, true);

    // Search.js gates with the shared LoginGate before rendering this
    if (!user) return null;

    const currentDetail = detailKey ? detail[detailKey] : null;

    // ---------- row renderers (people pages) ----------
    const renderUserRow = (u) => (
        <li key={u.id} className="os-user-row">
            <AvatarFrame user={u}>
                <img
                    src={getProfilePhotoSrc(u)}
                    alt={u.username}
                    onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                />
            </AvatarFrame>
            <div className="os-user-main">
                <span className="os-user-name">{u.username}</span>
                <span className="os-user-meta">
                    {u.location && <><Icon name="pin" /> {u.location}{u.distance != null ? ` · ${u.distance} mi` : ''}</>}
                    {u.copies > 1 && <> · ×{u.copies}</>}
                </span>
                {u.binderSize != null && (
                    <span className="os-user-meta">
                        {u.binderSize} in binder · {u.wishlistSize} wanted
                    </span>
                )}
                {(u.matchesYourWants > 0 || u.matchesYourHaves > 0) && (
                    <span className="os-user-fit">
                        {u.matchesYourWants > 0 &&
                            `has ${u.matchesYourWants} card${u.matchesYourWants !== 1 ? 's' : ''} you want`}
                        {u.matchesYourWants > 0 && u.matchesYourHaves > 0 && ' · '}
                        {u.matchesYourHaves > 0 && `wants ${u.matchesYourHaves} of yours`}
                    </span>
                )}
            </div>
            <div className="os-user-actions">
                <button className="os-btn ghost" onClick={() => navigate(`/profile/${u.id}`)}>View binder</button>
                <button className="os-btn gold" onClick={() => navigate('/trade', { state: { ownerId: u.id } })}>Send trade</button>
            </div>
        </li>
    );

    // Segmented activity bar under a printing in the card window -
    // each segment opens its own (shareable) page
    const segBar = (uuid, counts) => (
        <div className="os-seg">
            <button disabled={!counts.have} onClick={() => openView('have', uuid)}>
                {counts.have} have
            </button>
            <button disabled={!counts.want} onClick={() => openView('want', uuid)}>
                {counts.want} want
            </button>
        </div>
    );

    // Name tile: card facts (name/price/printings) up top; the site
    // trade-world strip sits visually apart at the tile's foot
    const nameTile = (card) => {
        const world = [];
        if (card.counts.have) world.push(`${card.counts.have} have`);
        if (card.counts.want) world.push(`${card.counts.want} want`);
        return (
            <button key={card.name} className="os-tile os-tile-click" onClick={() => openPrintings(card.name)}>
                <div className="os-tile-imgwrap">
                    {card.image_path
                        ? <img src={card.image_path} alt={card.name} loading="lazy" />
                        : <span className="os-tile-noimg">No image</span>}
                    {card.onWishlist
                        ? <span className="os-tile-wish">On your wishlist</span>
                        : card.theyWant
                            ? <span className="os-tile-wish theirs">They want yours</span>
                            : null}
                </div>
                <div className="os-tile-name">{card.name}</div>
                <div className="os-tile-price">
                    {card.min_price != null ? `from $${card.min_price.toFixed(2)}` : ''}
                    {card.counts.near !== undefined && card.counts.near > 0 && (
                        <span className="os-tile-near"><Icon name="pin" /> {card.counts.near} near</span>
                    )}
                </div>
                <div className="os-tile-line">{card.printings} printing{card.printings !== 1 ? 's' : ''}</div>
                {world.length > 0 && <div className="os-tile-world">{world.join(' · ')}</div>}
            </button>
        );
    };

    const showingResults = canSearch;

    // Active filters as removable chips above the results - the state
    // of the search is always readable (and undoable) in one place
    const setNameOf = (code) => allSets.find(s => s.setCode === code)?.setName || code;
    const priceSummary = filters.priceMin !== '' && filters.priceMax !== ''
        ? `$${filters.priceMin}–$${filters.priceMax}`
        : filters.priceMin !== '' ? `$${filters.priceMin}+` : `up to $${filters.priceMax}`;
    const chips = [];
    if (filters.avail) chips.push({
        key: 'avail', label: filters.avail === 'have' ? 'People have it' : 'People want it',
        clear: () => updateParams({ avail: null }),
    });
    if (near) chips.push({ key: 'near', label: `Within ${near} mi`, clear: () => updateParams({ near: null }) });
    filters.sets.forEach(c => chips.push({ key: `set-${c}`, label: setNameOf(c), clear: () => toggleListParam('sets', c) }));
    filters.rarities.forEach(r => chips.push({ key: `r-${r}`, label: r, cap: true, clear: () => toggleListParam('rarities', r) }));
    filters.finishes.forEach(fn => chips.push({ key: `f-${fn}`, label: fn, cap: true, clear: () => toggleListParam('finishes', fn) }));
    if (filters.priceMin !== '' || filters.priceMax !== '') chips.push({
        key: 'price', label: priceSummary, clear: () => updateParams({ pmin: null, pmax: null }),
    });

    // Printing context for the people-page subtitle
    const viewPrinting = viewUuid && printings
        ? printings.printings.find(p => p.uuid === viewUuid)
        : null;
    const viewTitle = viewKind === 'have' ? 'Who has' : 'Who wants';

    const sortSelect = (extraClass = '') => (
        <select
            className={`os-sort-select${extraClass}`}
            value={filters.sort}
            aria-label="Sort results"
            onChange={e => updateParams({ sort: e.target.value === 'relevance' ? null : e.target.value })}
        >
            {SORTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
    );

    return (
        <div className="onesearch">
            <div className="os-boxwrap">
                <input
                    className="os-box"
                    placeholder='Search any card… try "Lightning Bolt"'
                    value={q}
                    onChange={e => onType(e.target.value)}
                    autoFocus
                />
                {loading
                    ? <div className="os-spinner" />
                    : q && (
                        <button className="os-box-clear" onClick={() => onType('')} aria-label="Clear search">×</button>
                    )}
            </div>

            {/* Mobile only: the rail lives in a sheet, so its trigger and
                the sort sit right under the box, where filters belong */}
            {!viewKind && (
                <div className="os-mobilebar">
                    <button className={`os-mobile-filters${chips.length ? ' on' : ''}`} onClick={() => setSheetOpen(true)}>
                        <Icon name="sliders" /> Filters{chips.length ? ` · ${chips.length}` : ''}
                    </button>
                    {showingResults && !drillName && sortSelect(' os-sort-mobile')}
                </div>
            )}

            <div className="os-layout">
                {/* ---------------- Left rail ---------------- */}
                <FilterRail
                    key={railKey}
                    open={sheetOpen}
                    onClose={() => setSheetOpen(false)}
                    filters={filters}
                    near={near}
                    nearAvailable={data?.nearAvailable}
                    location={data?.location}
                    allSets={allSets}
                    ensureSets={ensureSets}
                    onPatch={updateParams}
                    onToggleList={toggleListParam}
                    onReset={resetAll}
                />

                {/* ---------------- Right: results ---------------- */}
                <div className="os-main">
                    {/* Active-filter chips (removable) + sort. Chips stay
                        visible over the card window too - they're why it
                        may show fewer printings. */}
                    {!viewKind && (chips.length > 0 || (showingResults && !drillName)) && (
                        <div className="os-results-bar">
                            {chips.length > 0 && (
                                <div className="os-active-chips">
                                    {chips.map(c => (
                                        <button
                                            key={c.key}
                                            className={`os-chip${c.cap ? ' cap' : ''}`}
                                            title="Remove"
                                            onClick={c.clear}
                                        >
                                            {c.label} ×
                                        </button>
                                    ))}
                                    {chips.length >= 2 && (
                                        <button className="os-chip clear" onClick={resetAll}>Clear all</button>
                                    )}
                                </div>
                            )}
                            {showingResults && !drillName && sortSelect(' os-sort-desktop')}
                        </div>
                    )}
                    {/* People page (own URL, shareable) */}
                    {drillName && viewKind && (
                        <>
                            <div className="os-drill-head">
                                <button className="results-back" onClick={closeView} aria-label="Back to card">←</button>
                                <div>
                                    <h3 className="os-drill-title">{viewTitle} {drillName}</h3>
                                    <p className="os-hint" style={{ margin: 0 }}>
                                        {viewPrinting
                                            ? `${viewPrinting.setName}${viewPrinting.min_price != null ? ` · $${viewPrinting.min_price.toFixed(2)}` : ''}`
                                            : viewUuid ? '' : 'any printing'}
                                        {near ? ` · within ${near} mi` : ''}
                                    </p>
                                </div>
                            </div>
                            <section className="os-section">
                                {!currentDetail ? (
                                    <p className="os-hint">Loading&hellip;</p>
                                ) : (
                                    (currentDetail.users || []).length === 0 ? (
                                        <p className="os-hint">Nobody{near ? ' nearby' : ''} right now.</p>
                                    ) : (
                                        <ul className="os-trade-list os-people-list">{currentDetail.users.map(renderUserRow)}</ul>
                                    )
                                )}
                            </section>
                        </>
                    )}

                    {/* Card window: every printing as the standard card */}
                    {drillName && !viewKind && (
                        <>
                            <div className="os-drill-head">
                                <button className="results-back" onClick={closeWindow} aria-label="Back to results">←</button>
                                <h3 className="os-drill-title">{drillName}</h3>
                            </div>
                            {printings && (
                                <div className="cards-grid os-window-grid">
                                    {printings.printings.map(pr => (
                                        <div className="os-window-cell" key={pr.uuid}>
                                            <UnifiedCard
                                                card={pr}
                                                mode="collection"
                                                setName={pr.setName}
                                            />
                                            {segBar(pr.uuid, pr.counts)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Search results */}
                    {!drillName && showingResults && cards.length > 0 && (
                        <div className="os-grid">
                            {cards.map(nameTile)}
                        </div>
                    )}

                    {!drillName && <div ref={sentinelRef} />}
                    {(loadingMore || (!showingResults && feedLoading && feed)) && (
                        <p className="os-hint" style={{ textAlign: 'center' }}>Loading more&hellip;</p>
                    )}

                    {/* Empty states */}
                    {showingResults && !loading && !drillName && data && cards.length === 0 && (
                        <div className="requests-placeholder">Nothing matched{q.trim() ? ` "${q.trim()}"` : ''} with these filters.</div>
                    )}

                    {/* The trade-world feed (no header - just the cards) */}
                    {!showingResults && !drillName && feed && feed.length > 0 && (
                        <div className="os-grid">
                            {feed.map(nameTile)}
                        </div>
                    )}
                    {!showingResults && !drillName && feed && feed.length === 0 && (
                        <div className="requests-placeholder">
                            No trade activity yet. Search any card to get started.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OneSearch;
